/**
 * Dashboard card when a recoverable exam simulation draft exists locally.
 *
 * @module SimulationResumeCard
 */

import { useNavigate } from 'react-router-dom';
import { ClipboardList, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { useExamSimulationStore } from '@/stores/useExamSimulationStore';
import {
  clearAllForUser,
  getNewestRecoverableDraft,
  type DraftIndexEntry,
} from '@/lib/simulationDraftStorage';
import { ROUTES } from '@/lib/routeConstants';
import type { ExamSimulationLocationState } from '@/lib/examSimulationNavState';
import type { QuickExamStartTarget } from '@/hooks/useQuickExamStartHref';
import type { TFunction } from 'i18next';
import { canStartSimulation, SIMULATION_RENEWAL_BLOCK_MESSAGE } from '@/lib/planAccess';

export interface SimulationResumeCardProps {
  readonly quickExamStart: QuickExamStartTarget;
}

function formatRelativeTime(updatedAt: number, locale: string): string {
  const diffMs = Date.now() - updatedAt;
  const diffMinutes = Math.max(1, Math.round(diffMs / 60_000));
  if (diffMinutes < 60) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
      -diffMinutes,
      'minute',
    );
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-diffHours, 'hour');
  }
  const diffDays = Math.round(diffHours / 24);
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-diffDays, 'day');
}

function buildDescription(draft: DraftIndexEntry, t: TFunction, locale: string): string {
  const moduleLabel = t(`exam.modules.${draft.module}`, { defaultValue: draft.module });
  const relative = formatRelativeTime(draft.updatedAt, locale);
  return t('simulation.recovery.description', {
    defaultValue: 'Pick up where you left off in the {{module}} module ({{when}}).',
    module: moduleLabel,
    when: relative,
  });
}

export function SimulationResumeCard({ quickExamStart }: SimulationResumeCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan, isLoading } = useCurrentPlan();
  const simulationAllowed = canStartSimulation(plan, user?.userType === 'admin');
  const reset = useExamSimulationStore((s) => s.reset);

  const draft = user?.id ? getNewestRecoverableDraft(user.id) : null;

  if (!draft || isLoading) {
    return null;
  }

  const description = buildDescription(draft, t, i18n.language || 'en');

  const handleStartFresh = () => {
    if (!user?.id) return;
    clearAllForUser(user.id);
    reset();
    navigate(quickExamStart.href);
  };

  const handleResume = () => {
    const state: ExamSimulationLocationState = { autoResumeFromDashboard: true };
    navigate(ROUTES.EXAM, { state });
  };

  const examLabel = `${draft.examType?.toUpperCase() ?? ''} · ${draft.level ?? ''}`.trim();

  return (
    <Card className="border-primary/25 bg-primary/5">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <ClipboardList className="h-5 w-5" aria-hidden />
            </div>
            <div className="space-y-1">
              <CardTitle className="font-ui text-lg">
                {t('dashboard.resumeSimulation.title', {
                  defaultValue: 'Resume simulation?',
                })}
              </CardTitle>
              <CardDescription className="font-ui text-sm">{description}</CardDescription>
              {!simulationAllowed ? (
                <p className="font-ui text-xs font-medium text-destructive">
                  {t('dashboard.resumeSimulation.renewRequired', {
                    defaultValue: SIMULATION_RENEWAL_BLOCK_MESSAGE,
                  })}
                </p>
              ) : null}
            </div>
          </div>
          {examLabel ? (
            <Badge variant="secondary" className="font-ui w-fit shrink-0">
              {examLabel}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2 pt-0">
        {simulationAllowed ? (
          <>
            <Button type="button" variant="outline" className="font-ui" onClick={handleStartFresh}>
              {t('simulation.recovery.startFresh', { defaultValue: 'Start fresh' })}
            </Button>
            <Button type="button" className="font-ui" onClick={handleResume}>
              {t('simulation.recovery.resume', { defaultValue: 'Resume' })}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            className="font-ui"
            onClick={() => navigate(`${ROUTES.SETTINGS}#plan`)}
          >
            <Lock className="mr-2 h-4 w-4" aria-hidden />
            {t('dashboard.planBanner.expired.cta', { defaultValue: 'Renew plan' })}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
